import { useEffect, useRef } from 'react';

type UseSmartPollingOptions = {
  isEnabled: boolean;
  pollIntervalMs: number;
  refetch: () => void;
  startPolling: (pollIntervalMs: number) => void;
  stopPolling: () => void;
};

export default function useSmartPolling({
  isEnabled,
  pollIntervalMs,
  refetch,
  startPolling,
  stopPolling,
}: UseSmartPollingOptions) {
  const lastPollTimestampRef = useRef<number>(0);

  // Detect when the user navigates away or returns from the browser tab
  const handleVisibilityChange = () => {
    if (!isEnabled) return;

    if (document.visibilityState === 'hidden') {
      // User navigated away from the browser tab, stop polling
      stopPolling();
    } else {
      const currentTimestamp = Date.now();
      const timeSinceLastPoll = currentTimestamp - lastPollTimestampRef.current;

      if (timeSinceLastPoll > pollIntervalMs) {
        refetch();
        lastPollTimestampRef.current = currentTimestamp;
      }

      // User is back on the browser tab, resume polling
      startPolling(pollIntervalMs);
    }
  };

  const handleCaptchaModal = () => {
    if (!isEnabled) return false;

    const captchaModal = document.getElementById('px-captcha-modal');
    const captchaContainer = document.querySelector('.px-captcha-container');

    if (captchaModal || captchaContainer) {
      stopPolling();
      return true;
    }
    return false;
  };

  useEffect(() => {
    if (isEnabled) {
      document.addEventListener('visibilitychange', handleVisibilityChange);
      const isCaptchaModalDisplayed = handleCaptchaModal();

      if (!isCaptchaModalDisplayed) {
        startPolling(pollIntervalMs);
      }
    }

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      stopPolling();
    };
  }, [isEnabled]);

  // Update last poll timestamp when polling occurs
  const updateLastPollTimestamp = () => {
    lastPollTimestampRef.current = Date.now();
  };

  return {
    updateLastPollTimestamp,
  };
}
